import React from "react";
import {
  Button,
  CircularProgress,
  Tooltip,
  type ButtonProps,
} from "@mui/material";
import {
  InterfaceDesignHeaderSlot,
  useInterfaceDesign,
} from "./InterfaceDesign.js";
import { designTokens, interfaceFont } from "./tokens.js";
import { MacIcon } from "./MacIcon.js";
import { designActionStyle } from "./designActionStyle.js";

/** Commands returning a promise stay busy until their work settles. */
export const DesignAction = React.forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, "onClick"> & {
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => unknown;
    icon?: string;
    originalAppearance?: boolean;
    tooltip?: React.ReactNode;
    tone?: "accent" | "success" | "error" | "warning" | "neutral";
  }
>(
  (
    { icon, tone, originalAppearance, tooltip, onClick, loading, ...props },
    ref,
  ) => {
    const [pending, setPending] = React.useState(false);
    const pendingRef = React.useRef(false);
    const busy = Boolean(loading || pending);
    const disabled = Boolean(props.disabled || busy);
    const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      if (pendingRef.current || disabled) return;
      pendingRef.current = true;
      try {
        const result = onClick?.(event);
        if (
          result &&
          typeof (result as PromiseLike<unknown>).then === "function"
        ) {
          setPending(true);
          await result;
        }
      } finally {
        pendingRef.current = false;
        setPending(false);
      }
    };
    const withTooltip = (button: React.ReactElement) =>
      tooltip ? (
        <Tooltip
          title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>}
        >
          <span
            style={{ display: "inline-flex" }}
            tabIndex={disabled ? 0 : undefined}
          >
            {button}
          </span>
        </Tooltip>
      ) : (
        button
      );
    const { mode, colorMode } = useInterfaceDesign();
    if (mode !== "modern" || originalAppearance)
      return withTooltip(
        <Button
          ref={ref}
          {...props}
          loading={busy}
          disabled={disabled}
          onClick={handleClick}
          aria-busy={busy || undefined}
        />,
      );
    const {
      sx,
      color: muiColor,
      variant,
      size,
      startIcon,
      endIcon,
      fullWidth,
      disableElevation,
      disableRipple,
      disableFocusRipple,
      focusVisibleClassName,
      component,
      href,
      classes,
      TouchRippleProps,
      ...domProps
    } = props;
    const color =
      tone ??
      (props.color === "success" ||
      props.color === "error" ||
      props.color === "warning"
        ? props.color
        : "accent");
    return withTooltip(
      <button
        {...domProps}
        ref={ref}
        type={props.type ?? "button"}
        className={`meet-mac-button ${props.className ?? ""}`}
        title={props.title}
        aria-label={props["aria-label"]}
        aria-pressed={props["aria-pressed"]}
        aria-busy={busy || undefined}
        disabled={disabled}
        onClick={handleClick}
        onKeyDown={props.onKeyDown}
        onMouseEnter={props.onMouseEnter}
        onTouchStart={props.onTouchStart}
        onTouchEnd={props.onTouchEnd}
        onMouseOver={props.onMouseOver}
        onMouseLeave={props.onMouseLeave}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        aria-describedby={props["aria-describedby"]}
        tabIndex={props.tabIndex}
        style={{
          ...designActionStyle(
            colorMode,
            color,
            Boolean(props.children),
            disabled,
          ),
          ...props.style,
        }}
      >
        {busy ? (
          <CircularProgress size={14} color="inherit" />
        ) : icon ? (
          <MacIcon name={icon} size={14} />
        ) : (
          props.startIcon
        )}
        {props.children}
        {props.endIcon}
      </button>,
    );
  },
);

/** Compact header commands retain the caller's handlers and accessibility props. */
export const MacHeaderAction = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { icon?: string }
>(({ icon, children, style, ...props }, ref) => {
  const { colorMode } = useInterfaceDesign();
  const p = designTokens[colorMode];
  return (
    <button
      {...props}
      ref={ref}
      type={props.type ?? "button"}
      className={`meet-mac-header-action ${props.className ?? ""}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        minHeight: 29,
        padding: children ? "5px 7px" : "5px",
        border: "1px solid transparent",
        borderRadius: 6,
        background: "transparent",
        color: p.muted,
        font: `500 11px ${interfaceFont}`,
        whiteSpace: "nowrap",
        cursor: props.disabled ? "default" : "pointer",
        flexShrink: 0,
        opacity: props.disabled ? 0.45 : 1,
        outlineOffset: 2,
        outlineColor: p.accent,
        ...style,
      }}
    >
      {icon && <MacIcon name={icon} size={16} />}
      {children}
    </button>
  );
});

export function MacHeader({
  title,
  name,
  nameContent,
  onNameClick,
  nameActionLabel,
  leading,
  status,
  actions,
}: {
  title: string;
  name?: string;
  nameContent?: React.ReactNode;
  onNameClick?: () => void;
  nameActionLabel?: string;
  leading?: React.ReactNode;
  status?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  const { colorMode } = useInterfaceDesign();
  const p = designTokens[colorMode];
  return (
    <header
      data-mac-header
      style={{
        height: 54,
        minHeight: 54,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "0 18px",
        background: p.chrome,
        borderBottom: `1px solid ${p.fine}`,
        color: p.text,
        font: `12px ${interfaceFont}`,
      }}
    >
      <style>{`
      [data-mac-header] .meet-mac-header-action:hover:not(:disabled) { background:${p.fill} !important; color:${p.text} !important; }
      [data-mac-header] .meet-header-path { display:flex; align-items:center; gap:11px; flex:1; min-width:0; }
      [data-mac-header] .meet-header-actions { display:flex; align-items:center; gap:12px; flex-shrink:0; }
      [data-mac-header] .meet-header-divider { width:1px; height:18px; background:${p.border}; flex-shrink:0; }
      @media(max-width:900px) {
        [data-mac-header] { gap:6px !important; padding:0 10px !important; }
        [data-mac-header] .meet-header-brand, [data-mac-header] .meet-header-chevron, [data-mac-header] .meet-header-status-label { display:none !important; }
        [data-mac-header] .meet-header-actions { gap:3px; }
      }
    `}</style>
      {leading}
      <div className="meet-header-path">
        <span
          className="meet-header-brand"
          style={{ color: p.muted, whiteSpace: "nowrap" }}
        >
          {title}
        </span>
        {nameContent ??
          (name && (
            <>
              <span className="meet-header-chevron" style={{ color: p.muted }}>
                <MacIcon name="chevron" size={11} />
              </span>
              {onNameClick ? (
                <button
                  type="button"
                  className="meet-mac-header-action"
                  onClick={onNameClick}
                  aria-label={nameActionLabel ?? name}
                  title={nameActionLabel ?? name}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    minWidth: 0,
                    maxWidth: "100%",
                    border: 0,
                    borderRadius: 5,
                    padding: "5px 7px",
                    background: "transparent",
                    color: p.text,
                    font: "inherit",
                    fontWeight: 560,
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {name}
                  </span>
                  <MacIcon name="down" size={12} />
                </button>
              ) : (
                <strong
                  title={name}
                  style={{
                    fontWeight: 560,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {name}
                </strong>
              )}
            </>
          ))}
      </div>
      <div className="meet-header-actions">
        {status}
        {actions}
        <span className="meet-header-divider" />
        <InterfaceDesignHeaderSlot />
      </div>
    </header>
  );
}
